import React, { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import { Drawer } from 'antd';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CircularProgress from '@mui/material/CircularProgress';
import TextInput from 'v3/Components/Form/TextInput';
import Dropdown, { DropdownOption } from 'v3/Components/Form/Dropdown';
import { PrimaryButton, TertiaryButton, LinkButton } from 'v3/Components/Shared/Buttons';
import { useToasts, useEnums } from 'Context';
import { buildEndpoint, useApiWorker, useDataPoster, useHasPermissionsV3 } from 'Utilities';
import { ENDPOINTS } from 'Models/Templates/Routes/Routes';
import { PermissionCodesV3 } from 'Models/Authorization/permissionCodesV3';
import { AxiosError } from 'axios';

const MAX_LOCATIONS = 8;

export type ImeLocation = {
  id: string;
  locationName?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  zipCode?: string;
};

// ── Drawer ─────────────────────────────────────────────────────────────────────

const EMPTY_OPTION: DropdownOption = { value: '', displayName: '' };

type FormValues = {
  locationName: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: DropdownOption;
  zipCode: string;
};

const INITIAL_VALUES: FormValues = {
  locationName: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  state: EMPTY_OPTION,
  zipCode: '',
};

type DrawerProps = {
  open: boolean;
  reviewerId?: string;
  location?: ImeLocation;
  onClose: () => void;
  onSaved: (location: ImeLocation) => void;
  onDeleted: (id: string) => void;
};

const ImeLocationDrawer: React.FC<DrawerProps> = ({
  open, reviewerId, location, onClose, onSaved, onDeleted,
}) => {
  const toast   = useToasts();
  const api     = useApiWorker('3.0');
  const { state: stateEnum } = useEnums();
  const isEdit  = !!location;

  const [saving,   setSaving]   = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [errors,   setErrors]   = useState<Partial<Record<keyof FormValues, string>>>({});

  const formik = useFormik<FormValues>({
    initialValues: INITIAL_VALUES,
    onSubmit: () => {},
  });

  useEffect(() => {
    if (!open) return;
    setErrors({});
    if (isEdit && location) {
      formik.setValues({
        locationName: location.locationName ?? '',
        addressLine1: location.addressLine1 ?? '',
        addressLine2: location.addressLine2 ?? '',
        city:         location.city ?? '',
        state:        location.state
          ? { value: location.state, displayName: location.state }
          : EMPTY_OPTION,
        zipCode:      location.zipCode ?? '',
      });
    } else {
      formik.resetForm();
    }
  }, [open, location?.id]);

  const [postAdd] = useDataPoster(
    buildEndpoint(ENDPOINTS.reviewers.base, reviewerId, 'imeLocations'),
    undefined, false, '3.0'
  );

  const [postUpdate] = useDataPoster(
    buildEndpoint(ENDPOINTS.reviewers.base, reviewerId, 'imeLocations', location?.id),
    undefined, true, '3.0'
  );

  const validate = (): boolean => {
    const next: Partial<Record<keyof FormValues, string>> = {};
    const v = formik.values;
    if (!v.addressLine1.trim()) next.addressLine1 = 'Address is required';
    if (!v.city.trim())         next.city         = 'City is required';
    if (!v.state.value)         next.state        = 'State is required';
    if (!v.zipCode.trim())      next.zipCode      = 'Zip Code is required';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = {
        locationName: formik.values.locationName.trim() || null,
        addressLine1: formik.values.addressLine1.trim(),
        addressLine2: formik.values.addressLine2.trim() || null,
        city:         formik.values.city.trim(),
        state:        String(formik.values.state.value ?? ''),
        zipCode:      formik.values.zipCode.trim(),
      };

      const resp: any = isEdit ? await postUpdate(payload) : await postAdd(payload);

      if (resp?.status === 200 || resp?.status === 201) {
        const saved: ImeLocation = {
          id:           resp.data?.id ?? location?.id ?? '',
          locationName: resp.data?.locationName ?? payload.locationName ?? undefined,
          addressLine1: resp.data?.addressLine1 ?? payload.addressLine1,
          addressLine2: resp.data?.addressLine2 ?? payload.addressLine2 ?? undefined,
          city:         resp.data?.city ?? payload.city,
          state:        resp.data?.state ?? payload.state,
          zipCode:      resp.data?.zipCode ?? payload.zipCode,
        };
        toast.success(isEdit ? 'Location updated' : 'Location added');
        onSaved(saved);
        onClose();
      } else if (resp?.response?.status === 400) {
        const data = resp.response?.data;
        const msg  = data?.message;
        if (msg) {
          toast.error(msg);
        } else if (data?.errors) {
          const mapped: Partial<Record<keyof FormValues, string>> = {};
          Object.entries(data.errors).forEach(([key, val]) => {
            const field = (key.charAt(0).toLowerCase() + key.slice(1)) as keyof FormValues;
            mapped[field] = (val as string[])[0];
          });
          setErrors(mapped);
          toast.error('Please validate the highlighted fields');
        }
      } else {
        toast.error('Failed to save. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!location) return;
    setDeleting(true);
    try {
      await api.delete(buildEndpoint(ENDPOINTS.reviewers.base, reviewerId, 'imeLocations', location.id));
      toast.success('Location removed');
      onDeleted(location.id);
      onClose();
    } catch {
      toast.error('Failed to delete. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  const drawerTitle = (
    <div className="ime-drawer__header-inner">
      <span className="ime-drawer__title">
        {isEdit ? 'Update Location' : 'Add Location'}
      </span>
      <button className="ime-drawer__close-btn" onClick={onClose} type="button">
        <CloseIcon />
      </button>
    </div>
  );

  const footer = (
    <div className="sl-drawer-footer">
      <TertiaryButton title="Cancel" onClick={onClose} disabled={saving || deleting} />
      <PrimaryButton
        title={saving ? undefined : 'Save'}
        onClick={handleSave}
        disabled={saving || deleting}
        style={{ width: 'auto', padding: '0 20px' }}
      >
        {saving && <CircularProgress size={14} thickness={5} style={{ color: 'inherit' }} />}
      </PrimaryButton>
    </div>
  );

  return (
    <Drawer
      visible={open}
      onClose={onClose}
      title={drawerTitle}
      width={560}
      closable={false}
      footer={footer}
      destroyOnClose
      className="ime-drawer sl-drawer"
    >
      <div className="ime-drawer__body">
        <p className="sl-drawer-description">
          Please provide the correct details about the reviewer's office locations.
        </p>

        <div className="ime-drawer__fields">
          <TextInput
            label="Location Name"
            value={formik.values.locationName}
            onChange={(e) => formik.setFieldValue('locationName', e.target.value)}
            error={errors.locationName}
            placeholder="e.g. St. Charles Hospital"
          />

          <div className="ime-drawer__row">
            <TextInput
              label="Address Line 1"
              required
              value={formik.values.addressLine1}
              onChange={(e) => { formik.setFieldValue('addressLine1', e.target.value); setErrors(p => ({ ...p, addressLine1: undefined })); }}
              error={errors.addressLine1}
            />
            <TextInput
              label="Address Line 2"
              value={formik.values.addressLine2}
              onChange={(e) => formik.setFieldValue('addressLine2', e.target.value)}
              error={errors.addressLine2}
            />
          </div>

          <div className="ime-drawer__row">
            <TextInput
              label="City"
              required
              value={formik.values.city}
              onChange={(e) => { formik.setFieldValue('city', e.target.value); setErrors(p => ({ ...p, city: undefined })); }}
              error={errors.city}
            />
            <Dropdown
              label="State"
              required
              enums={stateEnum}
              value={formik.values.state}
              onChange={(opt) => { formik.setFieldValue('state', opt); setErrors(p => ({ ...p, state: undefined })); }}
              error={errors.state}
              showIcons={false}
              sortAlphabetically
            />
          </div>

          <div className="ime-drawer__zip-row">
            <TextInput
              label="Zip Code"
              required
              value={formik.values.zipCode}
              onChange={(e) => { formik.setFieldValue('zipCode', e.target.value); setErrors(p => ({ ...p, zipCode: undefined })); }}
              error={errors.zipCode}
            />
          </div>
        </div>

        {isEdit && (
          <>
            <div className="unavailability-drawer__divider" style={{ marginTop: 32 }} />
            <button
              type="button"
              className="unavailability-drawer__delete-btn"
              onClick={handleDelete}
              disabled={deleting || saving}
            >
              {deleting
                ? <CircularProgress size={16} style={{ color: 'inherit' }} />
                : <CloseIcon fontSize="small" />
              }
              <span>Delete Location</span>
            </button>
          </>
        )}
      </div>
    </Drawer>
  );
};

// ── Card content ──────────────────────────────────────────────────────────────

type Props = {
  entries: ImeLocation[];
  reviewerId?: string;
  onRefresh: () => void;
};

const ImeLocationsContent: React.FC<Props> = ({ entries, reviewerId, onRefresh }) => {
  const hasPermissionsV3 = useHasPermissionsV3();
  const canManage = hasPermissionsV3(PermissionCodesV3.Reviewer.ImeLocation.Manage);

  const [drawerOpen,   setDrawerOpen]   = useState(false);
  const [activeEntry,  setActiveEntry]  = useState<ImeLocation | undefined>(undefined);
  const [localEntries, setLocalEntries] = useState<ImeLocation[]>(entries);

  useEffect(() => { setLocalEntries(entries); }, [entries]);

  const openAdd  = () => { setActiveEntry(undefined); setDrawerOpen(true); };
  const openEdit = (entry: ImeLocation) => { setActiveEntry(entry); setDrawerOpen(true); };
  const canAdd   = canManage && localEntries.length < MAX_LOCATIONS;

  const handleSaved = (saved: ImeLocation) => {
    setLocalEntries(prev => {
      const idx = prev.findIndex(e => e.id === saved.id);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = saved;
        return next;
      }
      return [...prev, saved];
    });
    onRefresh();
  };

  const handleDeleted = (id: string) => {
    setLocalEntries(prev => prev.filter(e => e.id !== id));
    onRefresh();
  };

  const formatAddress = (loc: ImeLocation) => {
    const parts = [loc.city, loc.state, loc.zipCode].filter(Boolean);
    return `${loc.addressLine1 ?? ''}${parts.length ? `, ${parts.join(', ')}` : ''}`;
  };

  return (
    <div className="overview-ime-locations">
      {canManage && (
        <div className="overview-ime-locations__add-top">
          {canAdd ? (
            <button type="button" className="overview-ime-locations__add-btn" onClick={openAdd}>
              <AddIcon fontSize="small" />
              Add Location
            </button>
          ) : (
            <span className="overview-ime-locations__limit-msg">
              Maximum of {MAX_LOCATIONS} locations reached
            </span>
          )}
        </div>
      )}

      {localEntries.length > 0 && (
        <div className="overview-ime-locations__list">
          {localEntries.map((loc) => (
            <div key={loc.id} className="overview-ime-locations__entry">
              <LocationOnIcon className="overview-ime-locations__icon" fontSize="small" />
              <div className="overview-ime-locations__entry-body">
                {loc.locationName && (
                  <p className="overview-ime-locations__name">{loc.locationName}</p>
                )}
                <p className="overview-ime-locations__address">{formatAddress(loc)}</p>
                {loc.addressLine2 && loc.addressLine2.trim() && (
                  <p className="overview-ime-locations__address">{loc.addressLine2}</p>
                )}
                {canManage && (
                  <LinkButton
                    onClick={() => openEdit(loc)}
                    className="overview-ime-locations__update"
                  >
                    Update
                  </LinkButton>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <ImeLocationDrawer
        key={activeEntry?.id ?? 'add'}
        open={drawerOpen}
        reviewerId={reviewerId}
        location={activeEntry}
        onClose={() => setDrawerOpen(false)}
        onSaved={handleSaved}
        onDeleted={handleDeleted}
      />
    </div>
  );
};

export default ImeLocationsContent;
